import { useState } from "react";
import { useNavigate } from "react-router";
import { useCreateExportFormat, type ExportFormat } from "../hooks/useExportFormats";
import { FormatSettingsForm } from "../export-formats/components/FormatSettingsForm";
import { FormatColumnsEditor, type FormatColumn } from "../export-formats/components/FormatColumnsEditor";
import { FormatStructureEditor } from "../export-formats/components/FormatStructureEditor.js";
import { FormatLayoutPreview } from "../export-formats/components/FormatLayoutPreview";
import { ConformancePanel } from "../export-formats/components/ConformancePanel";
import {
  PartnerProfileForm,
  orNull,
  type PartnerProfileValue,
} from "../export-formats/components/PartnerProfileForm";
import { type SchemaImportResult } from "../export-formats/components/SchemaImportModal";
import {
  flatColumnsToStructure,
  deriveColumnsFromStructure,
  validateFormatStructure,
  type FormatType,
  type ConformanceContract,
  type ExportFormatStructureNode,
} from "@capture/shared";
import "../admin.css";

const FORMAT_TYPES = [
  { value: "csv", label: "CSV", desc: "Comma-separated values" },
  { value: "fixed_width", label: "Fixed-Width", desc: "Positional columns" },
  { value: "xml", label: "XML", desc: "Structured markup" },
  { value: "json", label: "JSON", desc: "JavaScript object notation" },
  { value: "x937", label: "X9.37 Image Cash Letter (ICL)", desc: "Bank deposit file with embedded check images" },
];

type Step = "type" | "config" | "columns" | "save";

const WIZARD_STEPS: { id: Step; label: string }[] = [
  { id: "type", label: "Type" },
  { id: "config", label: "Settings" },
  { id: "columns", label: "Columns" },
  { id: "save", label: "Save" },
];

interface ExportFormatWizardPageProps {
  /**
   * R-201 / F4 — when true the wizard is mounted inside another surface (the
   * DestinationDetail format picker's "+ New format" slideover) rather than as
   * its own route. In embedded mode the final save calls `onCreated(format)`
   * instead of navigating away, and the back-to-list header chrome is hidden.
   */
  embedded?: boolean;
  /** Called with the created format after a successful save (embedded mode). */
  onCreated?: (format: ExportFormat) => void;
}

export function ExportFormatWizardPage({ embedded = false, onCreated }: ExportFormatWizardPageProps = {}) {
  const navigate = useNavigate();
  const { create, isSubmitting } = useCreateExportFormat();
  const [step, setStep] = useState<Step>("type");
  const [formatType, setFormatType] = useState("");
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [columns, setColumns] = useState<FormatColumn[]>([]);
  const [structure, setStructure] = useState<ExportFormatStructureNode[]>([]);
  // FR-001 — frozen conformance contract from the schema importer, if the user
  // switched on any policy. Mirrors the detail-page edit path so the wizard
  // doesn't silently drop enforcement.
  const [conformanceContract, setConformanceContract] = useState<ConformanceContract | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Task 3 (Partner Profile Completion) — optional, collapsed on the Save
  // step. Empty strings are sent as null (same convention as the detail page).
  const [partnerProfile, setPartnerProfile] = useState<PartnerProfileValue>({
    fileNamingPattern: "",
    partnerNotes: "",
    schemaAttachment: "",
    sampleData: "",
  });

  const isTreeFormat = formatType === "xml" || formatType === "json";

  // FR-001 / Task 5 (Partner Profile Completion) — a schema import carries
  // both the parsed conformance contract (may be null if no policy was
  // switched on) and the raw pasted text, stored as provenance in
  // schemaAttachment.
  const handleImport = (result: SchemaImportResult) => {
    setConformanceContract(result.contract);
    setPartnerProfile((prev) => ({ ...prev, schemaAttachment: result.rawSchemaText }));
  };

  const handleTypeSelect = (type: string) => {
    setFormatType(type);
    setConfig(getDefaultConfig(type));
    setStep("config");
  };

  const handleSave = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    if (isTreeFormat ? structure.length === 0 : columns.length === 0) {
      setError("At least one column is required — go back and add columns before saving.");
      return;
    }
    if (isTreeFormat) {
      const v = validateFormatStructure(structure);
      if (v.errors.length > 0) {
        setError(v.errors.map((e) => e.message).join("; "));
        return;
      }
    }
    setError(null);
    try {
      // Empty string clears/omits the field (sent as null) — same convention
      // as the detail-page edit path.
      const created = await create({
        name,
        formatType,
        config,
        structure: isTreeFormat ? structure : flatColumnsToStructure(columns),
        conformanceContract,
        fileNamingPattern: orNull(partnerProfile.fileNamingPattern),
        partnerNotes: orNull(partnerProfile.partnerNotes),
        schemaAttachment: orNull(partnerProfile.schemaAttachment),
        sampleData: orNull(partnerProfile.sampleData),
      });
      if (embedded) {
        onCreated?.(created);
      } else {
        navigate("/admin/export-formats");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const currentStepIndex = WIZARD_STEPS.findIndex((w) => w.id === step);

  return (
    <div className="admin-page">
      <div className="admin-header">
        {!embedded && (
          <button className="admin-back" onClick={() => navigate("/admin/export-formats")}>
            &larr; Export Formats
          </button>
        )}
        <h1 className="admin-title">New Export Format</h1>
      </div>

      {/* Step indicators — click to jump back to any earlier step */}
      <div className="wizard-steps">
        {WIZARD_STEPS.map(({ id: s, label }, i) => {
          const canJump = i <= currentStepIndex;
          const isDone = currentStepIndex > i;
          const className = `wizard-step ${step === s ? "wizard-step--active" : ""} ${
            isDone ? "wizard-step--done" : ""
          } ${canJump && step !== s ? "wizard-step--clickable" : ""}`;
          return (
            <button
              key={s}
              type="button"
              className={className}
              onClick={() => { if (canJump) setStep(s); }}
              disabled={!canJump}
              aria-current={step === s ? "step" : undefined}
            >
              <span className="wizard-step-num" aria-hidden="true">{isDone ? "✓" : i + 1}</span>
              <span className="wizard-step-label">{label}</span>
            </button>
          );
        })}
      </div>

      <div className="wizard-content">
        {step === "type" && (
          <div className="wizard-type-grid">
            {FORMAT_TYPES.map((ft) => (
              <button
                key={ft.value}
                className={`wizard-type-card ${formatType === ft.value ? "wizard-type-card--selected" : ""}`}
                onClick={() => handleTypeSelect(ft.value)}
              >
                <span className="wizard-type-label">{ft.label}</span>
                <span className="wizard-type-desc">{ft.desc}</span>
              </button>
            ))}
          </div>
        )}

        {step === "config" && (
          <div className="wizard-config">
            <FormatSettingsForm
              formatType={formatType as FormatType}
              value={config}
              onChange={setConfig}
            />
            <div className="wizard-nav">
              <button className="rb-btn" onClick={() => setStep("type")}>Back</button>
              <button className="rb-btn rb-btn-save" onClick={() => setStep("columns")}>Next</button>
            </div>
          </div>
        )}

        {step === "columns" && (
          <div className="wizard-columns">
            {(formatType === "xml" || formatType === "json") ? (
              <>
                <FormatStructureEditor value={structure} onChange={setStructure} format={formatType as "json" | "xml"} onImport={handleImport} />
                <ConformancePanel columns={deriveColumnsFromStructure(structure) as unknown as FormatColumn[]} contract={conformanceContract} />
              </>
            ) : (
              <>
                <FormatColumnsEditor
                  formatType={formatType as FormatType}
                  value={columns}
                  onChange={setColumns}
                  onImport={handleImport}
                  transactionGrain={config.transactionGrain as string | undefined}
                />
                <ConformancePanel columns={columns} contract={conformanceContract} />
              </>
            )}
            <FormatLayoutPreview
              formatType={formatType}
              config={config}
              columns={columns}
              structure={isTreeFormat ? structure : flatColumnsToStructure(columns)}
              sampleData={partnerProfile.sampleData}
            />
            <div className="wizard-nav">
              <button className="rb-btn" onClick={() => setStep("config")}>Back</button>
              <button className="rb-btn rb-btn-save" onClick={() => setStep("save")}>Next</button>
            </div>
          </div>
        )}

        {step === "save" && (
          <div className="wizard-save">
            <div className="rb-row">
              <label className="rb-label">Format Name</label>
              <input
                className="rb-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Accounting CSV"
              />
            </div>
            <div className="wizard-summary">
              <p><strong>Type:</strong> {formatType}</p>
              {isTreeFormat ? <p><strong>Fields:</strong> {structure.length}</p> : <p><strong>Columns:</strong> {columns.length}</p>}
            </div>
            {/* Task 3 (Partner Profile Completion) — optional, collapsed by
                default so it doesn't add friction to the common create flow.
                Inline summary styling mirrors SourceCodesSection's details
                pattern (no dedicated collapsible CSS class exists yet). */}
            <details style={{ marginTop: "var(--space-2)" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600, padding: "var(--space-2) 0" }}>
                Partner profile (optional)
              </summary>
              <PartnerProfileForm value={partnerProfile} onChange={setPartnerProfile} />
            </details>
            {error && <div className="admin-error">{error}</div>}
            <div className="wizard-nav">
              <button className="rb-btn" onClick={() => setStep("columns")}>Back</button>
              <button
                className="rb-btn rb-btn-save"
                onClick={handleSave}
                disabled={isSubmitting || !name.trim() || (isTreeFormat ? structure.length === 0 : columns.length === 0)}
                title={(isTreeFormat ? structure.length === 0 : columns.length === 0) ? "Add at least one column first" : undefined}
              >
                {isSubmitting ? "Saving..." : "Create Format"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function getDefaultConfig(formatType: string): Record<string, unknown> {
  switch (formatType) {
    case "csv":
      return { delimiter: ",", quoteChar: '"', escapeChar: '"', includeHeader: true, lineEnding: "lf", encoding: "utf-8", dateFormat: "YYYY-MM-DD", numberFormat: { decimalSeparator: ".", thousandsSeparator: "" }, lineItemMode: "exclude", emptyValue: "" };
    case "fixed_width":
      return { recordLength: 500, padChar: " ", lineEnding: "lf", encoding: "ascii", headerRecord: false, trailerRecord: false, dateFormat: "YYYYMMDD", numberFormat: { decimalHandling: "explicit", impliedDecimals: 0, signHandling: "none" } };
    case "xml":
      return { rootElement: "Batch", documentElement: "Document", encoding: "UTF-8", indentation: 2, includeSchema: false, dateFormat: "YYYY-MM-DD", lineItemElement: "LineItem", attributeFields: [] };
    case "json":
      return { prettyPrint: true, wrapInArray: true, dateFormat: "YYYY-MM-DD", numberAsString: true, nullHandling: "include", lineItemsField: "lineItems" };
    default:
      return {};
  }
}
